import { API_ENDPOINTS, HTTP_STATUS } from '../constants';
import {
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  Client,
  ClientMeResponse,
  UpdateClientRequest,
  ChangePasswordRequest,
  PasswordResetRequest,
  ActivateRequest,
} from '../types/api.types';
import { saveToken, clearToken, parseErrorMessage, AccountNotActivatedError } from '../utils';
import { API_BASE_URL, authenticatedFetch, simplePost } from './http.service';

/**
 * Login user. On 403 with code account_not_activated throws AccountNotActivatedError.
 */
export async function login(credentials: LoginRequest): Promise<AuthResponse> {
  const response = await fetch(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(credentials),
  });

  if (response.status === HTTP_STATUS.FORBIDDEN) {
    try {
      const body = await response.json();
      if (body?.code === 'account_not_activated') {
        throw new AccountNotActivatedError();
      }
    } catch (e) {
      if (e instanceof AccountNotActivatedError) throw e;
    }
    let message = await parseErrorMessage(response);
    if (message === 'Invalid email or password' || message.toLowerCase().includes('invalid email or password')) {
      message = 'Niepoprawne dane autoryzacyjne.';
    }
    throw new Error(message);
  }

  if (!response.ok) {
    let message = await parseErrorMessage(response);
    if (message === 'Invalid email or password' || message.toLowerCase().includes('invalid email or password')) {
      message = 'Niepoprawne dane autoryzacyjne.';
    }
    throw new Error(message);
  }

  const data: AuthResponse = await response.json();
  saveToken(data.token, data.expires_in);
  return data;
}

/**
 * Register new user. Backend sends activation code to email; no token in response.
 * Client must go to login and activate account with the code.
 */
export async function register(clientData: RegisterRequest): Promise<void> {
  const response = await simplePost(
    `${API_BASE_URL}${API_ENDPOINTS.REGISTER}`,
    clientData
  );
  if (!response.ok) {
    throw new Error(await parseErrorMessage(response));
  }
}

/**
 * Activate client account with email and 6-digit code from email.
 */
export async function activate(payload: ActivateRequest): Promise<void> {
  const response = await simplePost(
    `${API_BASE_URL}${API_ENDPOINTS.ACTIVATE}`,
    payload
  );
  if (!response.ok) {
    let message = await parseErrorMessage(response);
    if (message === 'Invalid activation code' || message.toLowerCase().includes('invalid activation code')) {
      message = 'Niepoprawny kod aktywacyjny.';
    }
    throw new Error(message);
  }
}

/**
 * Resend activation code to email. Only for inactive accounts.
 * API limit: 1 request per 5 minutes per email. Returns 429 when exceeded.
 */
export async function resendActivationCode(email: string): Promise<void> {
  const response = await simplePost(
    `${API_BASE_URL}${API_ENDPOINTS.RESEND_CODE}`,
    { email }
  );
  if (!response.ok) {
    const message = await parseErrorMessage(response);
    if (response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
      throw new Error('Kod można ponownie wysłać najwcześniej po 5 minutach.');
    }
    throw new Error(message);
  }
}

/**
 * Get current client data
 */
export async function getCurrentClient(signal?: AbortSignal): Promise<Client> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}${API_ENDPOINTS.ME}`,
    { method: 'GET', signal }
  );
  
  if (!response.ok) {
    if (response.status === 404 || response.status === 500) {
      clearToken();
      if (response.status === 500) {
        throw new Error(`Server error: Unable to verify session. Please log in again (${response.status})`);
      }
      throw new Error(`User session expired or user no longer exists (${response.status})`);
    }
    throw new Error(`HTTP error! status: ${response.status}`);
  }
  
  const data: ClientMeResponse = await response.json();
  return data.client;
}

/**
 * Logout user
 */
export async function logout(): Promise<void> {
  try {
    await authenticatedFetch(
      `${API_BASE_URL}${API_ENDPOINTS.LOGOUT}`,
      { method: 'POST' }
    );
  } catch (error) {
    console.error('Error logging out:', error);
  } finally {
    clearToken();
  }
}

/**
 * Update client profile
 */
export async function updateClient(clientData: UpdateClientRequest): Promise<Client> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}${API_ENDPOINTS.PROFILE}`,
    {
      method: 'PUT',
      body: JSON.stringify(clientData),
    }
  );
  
  if (!response.ok) {
    if (response.status === 404) {
      clearToken();
      throw new Error('User session expired or user no longer exists');
    }
    throw new Error(await parseErrorMessage(response));
  }
  
  // Endpoint returns 204 No Content on success, fetch updated data
  return getCurrentClient();
}

/**
 * Change user password
 */
export async function changePassword(passwordData: ChangePasswordRequest): Promise<void> {
  const response = await authenticatedFetch(
    `${API_BASE_URL}${API_ENDPOINTS.PASSWORD}`,
    {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    }
  );
  
  if (!response.ok) {
    if (response.status === 404) {
      clearToken();
      throw new Error('User session expired or user no longer exists');
    }
    throw new Error(await parseErrorMessage(response));
  }
  
  // Endpoint returns 204 No Content on success
}

/**
 * Request password reset - sends new password to email
 */
export async function requestPasswordReset(resetData: PasswordResetRequest): Promise<void> {
  const response = await simplePost(
    `${API_BASE_URL}${API_ENDPOINTS.PASSWORD_RESET}`,
    resetData
  );
  
  if (!response.ok) {
    if (response.status === 404) {
      throw new Error('Podany adres email nie istnieje w systemie');
    }
    throw new Error(await parseErrorMessage(response));
  }
  
  // Endpoint returns 204 No Content on success
}

